import { supabase } from '../lib/supabase'
import {
  handleQuery,
  accessDeniedResponse,
  resolveRoleContext,
  formatSupabaseError
} from './utils'

const RESPONSES_TABLE = 'marketing_form_responses'

const FORM_RESPONSES_LIST_SELECT = `
  *,
  marketing_forms (id, title, type),
  events (id, title),
  leads (
    id,
    full_name,
    email,
    phone,
    status,
    payment_status,
    agent_id,
    created_at,
    users!leads_agent_id_fkey (full_name, email, phone)
  )
`

/**
 * @param {{ role?: string, userId?: string }} [options]
 */
const requireAgentOrAdmin = async (options = {}) => {
  const roleContext = await resolveRoleContext(options)
  if (roleContext.error) return { error: roleContext.error }
  if (roleContext.role !== 'agent' && roleContext.role !== 'admin') {
    return {
      error: accessDeniedResponse('Only agents and administrators can access survey responses.')
        .error
    }
  }
  return { role: roleContext.role, userId: roleContext.userId, error: null }
}

/**
 * List form responses with forms, leads, lead owners, and events (RLS enforced).
 * @param {string} agentId - Reserved for API consistency; access is scoped by auth + RLS.
 * @param {{ formId?: string, role?: string, userId?: string }} [filters]
 */
export const getFormResponses = async (agentId, { formId, role, userId } = {}) => {
  const ctx = await requireAgentOrAdmin({ role, userId })
  if (ctx.error) return { data: null, error: ctx.error }

  if (ctx.role === 'agent' && agentId && ctx.userId && agentId !== ctx.userId) {
    return accessDeniedResponse('You can only load responses for your own agent account.')
  }

  let query = supabase
    .from(RESPONSES_TABLE)
    .select(FORM_RESPONSES_LIST_SELECT)
    .order('submitted_at', { ascending: false })

  if (formId) query = query.eq('form_id', formId)

  const result = await handleQuery(query, {
    fallbackMessage: 'Unable to load survey responses.'
  })

  if (result.error && result.error.code === 'PGRST200') {
    const fallbackSelect = FORM_RESPONSES_LIST_SELECT.replace(
      'users!leads_agent_id_fkey',
      'users!agent_id'
    )
    let fallbackQuery = supabase
      .from(RESPONSES_TABLE)
      .select(fallbackSelect)
      .order('submitted_at', { ascending: false })
    if (formId) fallbackQuery = fallbackQuery.eq('form_id', formId)
    return handleQuery(fallbackQuery, { fallbackMessage: 'Unable to load survey responses.' })
  }

  return result
}

/**
 * Users available as lead owners (RLS may restrict agents to their own row).
 * @param {{ role?: string, userId?: string }} [options]
 */
export const listLeadOwners = async (options = {}) => {
  const ctx = await requireAgentOrAdmin(options)
  if (ctx.error) return { data: null, error: ctx.error }

  return handleQuery(
    supabase
      .from('users')
      .select('id, full_name, email, role')
      .order('full_name', { ascending: true }),
    { fallbackMessage: 'Unable to load lead owners.' }
  )
}

/**
 * Create a lead from an unlinked form response and link the response.
 * @param {string} responseId
 * @param {{ full_name: string, email?: string, phone?: string, agent_id: string, status?: string, event_id?: string }} leadData
 * @param {{ role?: string, userId?: string }} [options]
 */
export const createLeadFromResponse = async (responseId, leadData, options = {}) => {
  const ctx = await requireAgentOrAdmin(options)
  if (ctx.error) return { data: null, error: ctx.error }

  if (!responseId) {
    return {
      data: null,
      error: { message: 'Response id is required.', code: 'invalid_input' }
    }
  }

  if (!leadData?.full_name?.trim()) {
    return {
      data: null,
      error: { message: 'Full name is required.', code: 'invalid_input' }
    }
  }

  if (!leadData?.agent_id) {
    return {
      data: null,
      error: { message: 'Lead owner is required.', code: 'invalid_input' }
    }
  }

  const { data: existing, error: fetchError } = await supabase
    .from(RESPONSES_TABLE)
    .select('id, lead_id, event_id')
    .eq('id', responseId)
    .maybeSingle()

  if (fetchError) {
    return { data: null, error: formatSupabaseError(fetchError, 'Unable to load the form response.') }
  }

  if (!existing) {
    return {
      data: null,
      error: { message: 'Form response not found.', code: 'not_found' }
    }
  }

  if (existing.lead_id) {
    return {
      data: null,
      error: { message: 'This response is already linked to a lead.', code: 'invalid_input' }
    }
  }

  const eventId = leadData.event_id ?? existing.event_id ?? null

  const leadPayload = {
    full_name: leadData.full_name.trim(),
    email: leadData.email?.trim() || null,
    phone: leadData.phone?.trim() || null,
    agent_id: leadData.agent_id,
    status: leadData.status ?? 'new',
    event_id: eventId
  }

  const { data: lead, error: insertError } = await supabase
    .from('leads')
    .insert(leadPayload)
    .select('id')
    .single()

  if (insertError) {
    return {
      data: null,
      error: formatSupabaseError(insertError, 'Unable to create the lead.')
    }
  }

  const linkResponse = async (selectClause) =>
    supabase
      .from(RESPONSES_TABLE)
      .update({ lead_id: lead.id })
      .eq('id', responseId)
      .select(selectClause)
      .maybeSingle()

  let linkResult = await linkResponse(FORM_RESPONSES_LIST_SELECT)

  if (linkResult.error?.code === 'PGRST200') {
    const fallbackSelect = FORM_RESPONSES_LIST_SELECT.replace(
      'users!leads_agent_id_fkey',
      'users!agent_id'
    )
    linkResult = await linkResponse(fallbackSelect)
  }

  if (linkResult.error) {
    return {
      data: null,
      error: formatSupabaseError(
        linkResult.error,
        'Lead was created but linking the response failed.'
      )
    }
  }

  return { data: linkResult.data, error: null }
}
